import React from 'react';
import { Button } from 'ui-toolkit';
import { createOnChangeHandlerGenerator } from '../utilities/createOnChangeHandlerGenerator';
import CdNumberInput from '../CdNumberInput.styles';
import Currency from '../Currency';
import IHasItemDataProps from './IHasItemDataProps';
import IHasItemEventProps from './IHasItemEventProps';
import { IItemWithoutShowSellerProps } from './IItemProps';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faSort } from '@fortawesome/pro-solid-svg-icons/faSort';
import { StyledFontAwesomeThatCanBeDisabled } from '../../shared/permissions/StyledFontAwesomeThatCanBeDisabled';
import { IBaseCDV3ViewItem } from '../../../ui-toolkit/cdItems/interfaces/ICDViewItem';

type AmountDisplayType = 'input' | 'none' | 'text';

interface ISharedPurchaseItemCellsProps<T>
  extends IItemWithoutShowSellerProps,
    IHasItemDataProps<T>,
    IHasItemEventProps<T> {
  buyerAtClosingDisplay?: AmountDisplayType;
  buyerBeforeClosingDisplay?: AmountDisplayType;
  className?: string;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: () => void;
  sellerAtClosingDisplay?: AmountDisplayType;
  paidByOthersDisplay?: AmountDisplayType;
  hasEditPermissions?: boolean;
  children?: React.ReactNode;
}

export default class SharedPurchaseItemCells<T extends IBaseCDV3ViewItem> extends React.Component<
  ISharedPurchaseItemCellsProps<T>
> {
  public render() {
    const {
      buyerAtClosingDisplay,
      buyerBeforeClosingDisplay,
      children,
      className,
      data,
      onBlur,
      onChange,
      onFocus,
      onRemove,
      paidByOthersDisplay,
      sellerAtClosingDisplay,
      showRemove,
      showMove,
      hasEditPermissions,
    } = this.props;
    const noDisplaysIsInput =
      buyerAtClosingDisplay !== 'input' &&
      buyerBeforeClosingDisplay !== 'input' &&
      sellerAtClosingDisplay !== 'input' &&
      paidByOthersDisplay !== 'input';
    const buyerAtClosing = data.buyer_at_closing;
    const buyerBeforeClosing = data.buyer_before_closing;
    const sellerAtClosing = data.seller_at_closing;
    const paidByOthers = data.paid_by_others;
    const generateDefaultOnChangeHandler = createOnChangeHandlerGenerator(data, onChange);
    const onPaidByOthersChangeEvent = generateDefaultOnChangeHandler('paid_by_others');
    const onSellerAtClosingChangeEvent = generateDefaultOnChangeHandler('seller_at_closing');
    const onBuyerAtClosingChangeEvent = generateDefaultOnChangeHandler('buyer_at_closing');
    const onBuyerBeforeClosingChangeEvent = generateDefaultOnChangeHandler('buyer_before_closing');
    const onRemoveHandler = () => {
      if (onRemove) onRemove(data);
    };
    return (
      <div className={`cost-data__row cost-data__aligned-row ${className}`}>
        <div className="cost-data__content-cell cost-data__content-row-number" />
        <div className="cost-data__content-cell cost-data__content-name">{children}</div>
        <div
          className={`cost-data__content-cell cost-data__content-amount ${
            noDisplaysIsInput ? ' cost-data__content-amount--text-content' : ''
          }`}
        >
          {buyerAtClosingDisplay === 'input' ? (
            <CdNumberInput
              mode="currency"
              onFocus={onFocus}
              onBlur={onBlur}
              onChange={onBuyerAtClosingChangeEvent}
              value={data.buyer_at_closing}
              disabled={!hasEditPermissions}
            />
          ) : buyerAtClosingDisplay === 'text' ? (
            <Currency value={buyerAtClosing} />
          ) : null}
          {buyerBeforeClosingDisplay === 'input' ? (
            <CdNumberInput
              mode="currency"
              onFocus={onFocus}
              onBlur={onBlur}
              onChange={onBuyerBeforeClosingChangeEvent}
              value={data.buyer_before_closing}
              disabled={!hasEditPermissions}
            />
          ) : buyerBeforeClosingDisplay === 'text' ? (
            <Currency value={buyerBeforeClosing} />
          ) : null}
          {sellerAtClosingDisplay === 'input' ? (
            <CdNumberInput
              mode="currency"
              onFocus={onFocus}
              onBlur={onBlur}
              onChange={onSellerAtClosingChangeEvent}
              value={data.seller_at_closing}
              disabled={!hasEditPermissions}
            />
          ) : sellerAtClosingDisplay === 'text' ? (
            <Currency value={sellerAtClosing} />
          ) : null}
          {paidByOthersDisplay === 'input' ? (
            <CdNumberInput
              mode="currency"
              onFocus={onFocus}
              onBlur={onBlur}
              onChange={onPaidByOthersChangeEvent}
              value={data.paid_by_others}
              disabled={!hasEditPermissions}
            />
          ) : paidByOthersDisplay === 'text' ? (
            <Currency value={paidByOthers} />
          ) : null}
        </div>
        <div className="cost-data__content-cell cost-data__content-control">
          {showRemove === undefined || showRemove ? (
            <Button intent="danger" isInline onClick={onRemoveHandler} disabled={!hasEditPermissions}>
              <StyledFontAwesomeThatCanBeDisabled icon={faTrash} disabled={!hasEditPermissions} />
            </Button>
          ) : null}
          {showMove ? (
            <Button intent="ghost" isInline disabled={!hasEditPermissions}>
              <StyledFontAwesomeThatCanBeDisabled icon={faSort} disabled={!hasEditPermissions} />
            </Button>
          ) : null}
        </div>
      </div>
    );
  }
}
